<template>
  <div id="opportunities">
    <header class="head">
      <div>
        <h1 class="head__title">Opportunities</h1>
        <p class="head__sub">Every company currently raising, open to your treasury.</p>
      </div>
      <router-link class="head__back" :to="{ name: 'Invest' }">
        <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <path d="M10 4L6 8l4 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        Treasury
      </router-link>
    </header>

    <div class="toolbar">
      <!-- What kind of deal: a note, a share, a fund, a metal, an NGX stock. -->
      <select v-model="typeFilter" class="type-filter" aria-label="Filter by deal type">
        <option value="">All deal types</option>
        <option v-for="type in DEAL_TYPES" :key="type.tone" :value="type.tone">{{ type.label }}</option>
      </select>
      <Search
        @search-input="checkSearchQuery"
        type="name"
        :showSearchField="true"
      />
    </div>

    <!-- No `limit`, so this renders the full paginated list — the preview on the Treasury
         overview is the same component capped at three. -->
    <RaisingTable :hasSearchQuery="searchParam.length > 0" :typeFilter="typeFilter" />
  </div>
</template>

<script>
/**
 * The full "now raising" listing.
 *
 * Split out when the Treasury overview became a dashboard: its Now Raising block shows three
 * and links here, and before this page existed that link pointed back at the overview itself.
 */
import Search from "@/components/general/Search";
import RaisingTable from "@/components/tables/RaisingTable";
import { mapActions } from "vuex";

export default {
  name: "Opportunities",

  components: { Search, RaisingTable },

  data() {
    return {
      searchParam: "",
      typeFilter: "",
      DEAL_TYPES: [
        { tone: "debt", label: "Debt" },
        { tone: "equity", label: "Equity" },
        { tone: "safe", label: "SAFE" },
        { tone: "fund", label: "Funds" },
        { tone: "commodity", label: "Commodities" },
        { tone: "ngx", label: "NGX stocks" },
      ],
    };
  },

  watch: {
    searchParam(val) {
      // Clearing the box should restore the full list, not leave the last search on screen.
      // With a filter on, the whole list is loaded so the filter narrows all of it.
      if (val) this.findToken();
      else this.getCompaniesRaising({ page: 1, limit: this.typeFilter ? 100 : 10 });
    },
  },

  methods: {
    ...mapActions("TokenModule", ["searchToken", "getCompaniesRaising"]),

    checkSearchQuery(query) {
      this.searchParam = query.value;
    },

    async findToken() {
      // Only open raises here; completed ones live on the Exchange page.
      await this.searchToken({ name: this.searchParam, completed_raise: false });
    },
  },
};
</script>

<style lang="scss" scoped>
#opportunities {
  padding-bottom: 56px;
  font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif;
}

.head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 20px;

  &__title {
    margin: 0;
    font-size: 22px;
    font-weight: 600;
    color: #0f172a;
  }

  &__sub {
    margin: 4px 0 0;
    font-size: 14px;
    color: #64748b;
  }

  &__back {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 13px;
    font-weight: 500;
    color: #4d84ff;
    text-decoration: none;
    transition: color 150ms ease;

    svg {
      width: 14px;
      height: 14px;
    }

    &:hover {
      color: #3b6fe8;
    }
  }
}

.toolbar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;
}

.type-filter {
  padding: 9px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  background: #ffffff;
  color: #0f172a;
  font-size: 14px;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid #4d84ff;
    outline-offset: 1px;
  }
}
</style>
